import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageShell from "@/components/PageShell";
import { fmtMoney, fmtDate, EmptyState } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { PlugsConnected, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import useSmartPoll from "@/lib/useSmartPoll";

export default function DisconnectedClients() {
  const { t, tReason, lang } = useI18n();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clients", { params: { status: "disconnected" } });
      setClients(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  // Mutual sync — smart polling (pauses while tab is hidden).
  useSmartPoll(load, 60000);

  const reactivate = async (id) => {
    if (!confirm(t("reconnect.confirm"))) return;
    setBusyId(id);
    try {
      await api.post(`/clients/${id}/status`, { status: "active" });
      toast.success(t("reconnect.success"));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <PageShell
      section={t("nav.group.operation")}
      page={t("disc.title")}
      context={`${clients.length} ${clients.length === 1 ? t("common.client") : t("common.clients")}`}
      testid="disc-toolbar"
    >
      <div className="mb-6 border-l-2 border-red-500 pl-4 py-2">
        <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">{t("disc.title")}</div>
        <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{t("disc.subtitle")}</div>
      </div>

      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800/40 border-b border-slate-200 dark:border-slate-800">
            <tr className="text-left">
              <th className="px-5 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500 dark:text-slate-400">{t("disc.col.client")}</th>
              <th className="px-5 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500 dark:text-slate-400">{t("disc.col.reason")}</th>
              <th className="px-5 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500 dark:text-slate-400">{t("disc.col.since")}</th>
              <th className="px-5 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500 dark:text-slate-400">{t("disc.col.by")}</th>
              <th className="px-5 py-3 text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500 dark:text-slate-400 text-right">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {!loading && clients.length === 0 && (
              <tr><td colSpan={5}>
                <EmptyState icon={PlugsConnected} title={t("disc.empty")} message={t("disc.subtitle")} />
              </td></tr>
            )}
            {clients.map((c) => (
              <tr
                key={c.id}
                data-testid={`disc-row-${c.id}`}
                className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
              >
                <td className="px-5 py-4">
                  <div className="font-medium text-slate-900 dark:text-slate-100 flex items-center gap-2">
                    <Warning size={14} weight="fill" className="text-red-500" />
                    {c.name}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{c.email || "—"} · {c.phone || ""}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{c.address || "—"}</div>
                </td>
                <td className="px-5 py-4 text-slate-700 dark:text-slate-300">
                  <div className="text-sm">{tReason(c.disconnection_reason_code, c.disconnection_reason_label) || "—"}</div>
                  {c.disconnection_reason_note && (
                    <div className="text-xs text-slate-500 dark:text-slate-400 mt-1 italic">"{c.disconnection_reason_note}"</div>
                  )}
                </td>
                <td className="px-5 py-4 text-slate-600 dark:text-slate-300 font-mono text-xs">
                  {c.disconnected_at ? fmtDate(c.disconnected_at) : "—"}
                </td>
                <td className="px-5 py-4">
                  <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 border ${
                    c.disconnected_by === "support"
                      ? "border-amber-300 text-amber-700 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700 dark:text-amber-300"
                      : "border-slate-300 text-slate-700 bg-slate-50 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300"
                  }`}>
                    {c.disconnected_by === "support" ? t("disc.actor.support") : t("disc.actor.financial")}
                  </span>
                </td>
                <td className="px-5 py-4 text-right whitespace-nowrap">
                  <Button
                    onClick={() => reactivate(c.id)}
                    disabled={busyId === c.id}
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white h-8"
                    data-testid={`reactivate-${c.id}`}
                  >
                    <PlugsConnected size={14} className="mr-1.5" />
                    {t("clients.action.reconnect")}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 text-xs text-slate-500 dark:text-slate-400">
        {lang === "es"
          ? "Esta lista se sincroniza con Service en tiempo real (refresh automático cada 15 s)."
          : "This list syncs with Service in real time (auto-refresh every 15s)."}
      </div>
    </PageShell>
  );
}
